import * as React from 'react';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { format as formatDate, parseISO } from 'date-fns';
import { defaultDateFormat } from '../../../core/components/Fields/DateField.tsx';
import { getCurrentLocale } from '../../../core/utils/localization.ts';
import classnames from '../../../core/utils/classnames.tsx';
import { withIcon } from '../../../core/components/Icon.tsx';
import { faArrowUpRightFromSquare, faCircleInfo } from '@fortawesome/pro-regular-svg-icons';

const InfoIcon = withIcon(faCircleInfo);
const ExternalLinkIcon = withIcon(faArrowUpRightFromSquare);

/**
 * A readable rendering of an e-invoice (XRechnung, ZUGFeRD) from the parsed source the backend stores on
 * the record. There is no page image for a pure XML invoice, so this stands in for the document the
 * reviewer would otherwise compare the form against. It is deliberately independent of the editable
 * form: it shows what the sender stated, not what the reviewer has changed.
 */

type Address = {
    line_one?: string | null;
    line_two?: string | null;
    line_three?: string | null;
    postcode?: string | null;
    city?: string | null;
    country_code?: string | null;
};

type Party = {
    id?: string | null;
    name?: string | null;
    address?: Address | null;
    contact?: { department?: string | null; phone?: string | null; email?: string | null } | null;
    tax_registration?: { vat_id?: string | null; tax_number?: string | null } | null;
    legal_registration_id?: string | null;
    electronic_address?: string | null;
};

type TaxBreakdown = {
    category_code?: string | null;
    rate?: string | null;
    taxable_amount?: string | null;
    tax_amount?: string | null;
    exemption_reason?: string | null;
};

type PaymentDiscountTerms = {
    percentage?: string | null;
    days?: string | null;
    base_amount?: string | null;
    amount?: string | null;
};

type Attachment = {
    id?: string | null;
    type_code?: string | null;
    description?: string | null;
    filename?: string | null;
    mime_code?: string | null;
    embedded?: boolean;
};

type AllowanceCharge = {
    is_charge: boolean;
    amount: string;
    reason?: string | null;
    reason_code?: string | null;
    percentage?: string | null;
    base_amount?: string | null;
    tax_rate?: string | null;
};

type LineItem = {
    line_id?: string | null;
    product_name?: string | null;
    product_id?: string | null;
    global_id?: string | null;
    quantity?: string | null;
    unit_code?: string | null;
    unit_price?: string | null;
    price_base_quantity?: string | null;
    line_total?: string | null;
    tax_rate?: string | null;
    allowances_charges?: AllowanceCharge[];
    description?: string | null;
    buyer_item_id?: string | null;
    order_line_reference?: string | null;
    gross_unit_price?: string | null;
    price_allowance_amount?: string | null;
    origin_country?: string | null;
    period_start?: string | null;
    period_end?: string | null;
};

export type EInvoiceData = {
    format: string;
    specification_id?: string | null;
    is_credit_note?: boolean;
    invoice: {
        invoice_id?: string | null;
        issue_date?: string | null;
        due_date?: string | null;
        delivery_date?: string | null;
        currency?: string | null;
        document_type_code?: string | null;
        seller?: Party | null;
        buyer?: Party | null;
        delivery_party?: Party | null;
        order_reference?: string | null;
        delivery_note_reference?: string | null;
        contract_reference?: string | null;
        buyer_reference?: string | null;
        line_total?: string | null;
        tax_total?: string | null;
        tax_rate?: string | null;
        grand_total?: string | null;
        line_items?: LineItem[];
        allowances_charges?: AllowanceCharge[];
        allowance_total?: string | null;
        charge_total?: string | null;
        tax_exclusive_amount?: string | null;
        tax_inclusive_amount?: string | null;
        prepaid_amount?: string | null;
        rounding_amount?: string | null;
        payable_amount?: string | null;
        seller_order_reference?: string | null;
        despatch_advice_reference?: string | null;
        order_date?: string | null;
        invoice_period_start?: string | null;
        invoice_period_end?: string | null;
        delivery_terms?: string | null;
        tax_breakdown?: TaxBreakdown[];
        payment_discount_terms?: PaymentDiscountTerms[];
        attachments?: Attachment[];
        payment_means?: {
            type_code?: string | null;
            iban?: string | null;
            bic?: string | null;
            account_name?: string | null;
            account_id?: string | null;
            information?: string | null;
        } | null;
        payment_terms?: {
            description?: string | null;
            due_date?: string | null;
            payment_reference?: string | null;
        } | null;
        note?: string | null;
    };
};

const FORMAT_LABELS: Record<string, string> = {
    XRECHNUNG_UBL: 'XRechnung',
    EN16931_UBL: 'EN 16931 (UBL)',
    ZUGFERD_CII: 'ZUGFeRD / Factur-X',
};

const toNumber = (value: string | null | undefined): number | undefined => {
    if (value == null || value === '') return undefined;
    const number = Number(value);
    return Number.isNaN(number) ? undefined : number;
};

type Formatters = {
    money: (value: string | null | undefined) => string;
    number: (value: string | null | undefined) => string;
    percent: (value: string | null | undefined) => string;
    date: (value: string | null | undefined) => string;
};

// Intl.NumberFormat construction is the expensive part; build the formatters once per currency, not per cell.
const useFormatters = (currency: string): Formatters =>
    React.useMemo(() => {
        const locale = getCurrentLocale();
        // The XML states amounts exactly (0.0397 per piece, 75.0000 pieces); the view must not round them, so the
        // formatters keep every fraction digit the value has and only add the locale's separators.
        const exact = { maximumFractionDigits: 20 };
        const numberFormat = new Intl.NumberFormat(locale, exact);
        let moneyFormat: Intl.NumberFormat | undefined;
        try {
            moneyFormat = new Intl.NumberFormat(locale, {
                style: 'currency',
                currency,
                minimumFractionDigits: 2,
                ...exact,
            });
        } catch {
            moneyFormat = undefined; // unknown currency code: fall back to a plain number with the code appended
        }
        const format = (formatter: Intl.NumberFormat | undefined, value: string | null | undefined, suffix = '') => {
            const number = toNumber(value);
            if (number === undefined) return value ?? '';
            return formatter ? formatter.format(number) : `${number.toFixed(2)} ${suffix}`.trim();
        };
        return {
            money: (value) => format(moneyFormat, value, currency),
            number: (value) => format(numberFormat, value),
            percent: (value) =>
                toNumber(value) === undefined ? (value ?? '') : `${numberFormat.format(toNumber(value)!)} %`,
            date: (value) => {
                if (!value) return '';
                try {
                    return formatDate(parseISO(value), defaultDateFormat);
                } catch {
                    return value;
                }
            },
        };
    }, [currency]);

const addressLines = (address: Address | null | undefined): string[] => {
    if (!address) return [];
    const cityLine = [address.postcode, address.city].filter(Boolean).join(' ');
    return [address.line_one, address.line_two, address.line_three, cityLine, address.country_code].filter(
        (line): line is string => !!line
    );
};

const describeAllowanceCharge = (entry: AllowanceCharge, t: (key: string) => string, fmt: Formatters) => {
    const kind = entry.is_charge ? t('eInvoiceView.lines.charge') : t('eInvoiceView.lines.allowance');
    const reason = [entry.reason, entry.reason_code && `(${entry.reason_code})`].filter(Boolean).join(' ');
    const parts = [reason ? `${kind}: ${reason}` : kind];
    if (entry.percentage) {
        const basis = entry.base_amount ? ` ${t('eInvoiceView.lines.of')} ${fmt.number(entry.base_amount)}` : '';
        parts.push(`${fmt.percent(entry.percentage)}${basis}`);
    }
    return parts.join(' · ');
};

const signedMoney = (entry: AllowanceCharge, fmt: Formatters) =>
    `${entry.is_charge ? '+' : '−'}${fmt.money(entry.amount)}`;

const formatPeriod = (start: string | null | undefined, end: string | null | undefined, fmt: Formatters) => {
    if (!start && !end) return undefined;
    if (start && end && start !== end) return `${fmt.date(start)} – ${fmt.date(end)}`;
    return fmt.date(start || end);
};

const lineIdentifiers = (item: LineItem, t: (key: string) => string): string[] =>
    [
        item.product_id,
        item.global_id,
        item.buyer_item_id && `${t('eInvoiceView.lines.buyerItemId')} ${item.buyer_item_id}`,
        item.order_line_reference && `${t('eInvoiceView.lines.orderLine')} ${item.order_line_reference}`,
        item.origin_country && `${t('eInvoiceView.lines.origin')} ${item.origin_country}`,
    ].filter((part): part is string => !!part);

const hasPriceDiscount = (item: LineItem) => {
    const gross = toNumber(item.gross_unit_price);
    const net = toNumber(item.unit_price);
    return !!gross && net !== undefined && gross !== net;
};

const attachmentDetails = (attachment: Attachment, t: (key: string) => string) =>
    [
        attachment.mime_code,
        t(attachment.embedded ? 'eInvoiceView.attachments.embedded' : 'eInvoiceView.attachments.referenced'),
    ]
        .filter(Boolean)
        .join(', ');

const describeDiscountTerms = (terms: PaymentDiscountTerms, t: TFunction, fmt: Formatters) => {
    const vars = { percent: fmt.percent(terms.percentage), days: fmt.number(terms.days) || '–' };
    if (terms.amount && terms.base_amount) {
        return t('eInvoiceView.payment.discountLineWithAmount', {
            ...vars,
            amount: fmt.money(terms.amount),
            base: fmt.money(terms.base_amount),
        });
    }
    return t('eInvoiceView.payment.discountLine', vars);
};

const describeTax = (tax: TaxBreakdown, t: (key: string) => string, fmt: Formatters) => {
    const parts = [t('eInvoiceView.totals.tax')];
    if (tax.rate) parts.push(fmt.percent(tax.rate));
    if (tax.taxable_amount) parts.push(`${t('eInvoiceView.totals.taxOn')} ${fmt.money(tax.taxable_amount)}`);
    if (tax.exemption_reason) parts.push(`(${t('eInvoiceView.totals.exempt')}: ${tax.exemption_reason})`);
    return parts.join(' ');
};

const PartyBlock = ({ title, party }: { title: string; party: Party | null | undefined }) => {
    const { t } = useTranslation('assistance');
    if (!party || (!party.name && !party.address)) return null;

    return (
        <div className="min-w-0">
            <div className="text-xs uppercase tracking-wide text-content-medium mb-1">{title}</div>
            {party.name && <div className="font-semibold text-content-strong break-words">{party.name}</div>}
            {addressLines(party.address).map((line, index) => (
                <div key={index} className="text-content-strong break-words">
                    {line}
                </div>
            ))}
            {(party.id ||
                party.tax_registration?.vat_id ||
                party.legal_registration_id ||
                party.electronic_address ||
                party.contact) && (
                <dl className="mt-2 text-xs text-content-medium space-y-0.5">
                    {party.id && (
                        <div>
                            <dt className="inline">{t('eInvoiceView.party.id')}: </dt>
                            <dd className="inline text-content-strong">{party.id}</dd>
                        </div>
                    )}
                    {party.tax_registration?.vat_id && (
                        <div>
                            <dt className="inline">{t('eInvoiceView.party.vatId')}: </dt>
                            <dd className="inline text-content-strong">{party.tax_registration.vat_id}</dd>
                        </div>
                    )}
                    {party.legal_registration_id && (
                        <div>
                            <dt className="inline">{t('eInvoiceView.party.registration')}: </dt>
                            <dd className="inline text-content-strong">{party.legal_registration_id}</dd>
                        </div>
                    )}
                    {party.electronic_address && (
                        <div>
                            <dt className="inline">{t('eInvoiceView.party.electronicAddress')}: </dt>
                            <dd className="inline text-content-strong">{party.electronic_address}</dd>
                        </div>
                    )}
                    {party.contact?.department && <div className="text-content-strong">{party.contact.department}</div>}
                    {party.contact?.email && <div className="text-content-strong">{party.contact.email}</div>}
                    {party.contact?.phone && <div className="text-content-strong">{party.contact.phone}</div>}
                </dl>
            )}
        </div>
    );
};

type MetaRowProps = {
    label: string;
    value: string | null | undefined;
    /** Total lines: separated from the rows above and set in a larger, bold face. */
    emphasis?: 'subtotal' | 'total';
};

const MetaRow = ({ label, value, emphasis }: MetaRowProps) => {
    if (!value) return null;
    return (
        <div
            className={classnames(
                'flex justify-between gap-4',
                emphasis === 'total' && 'pt-2 mt-2 border-t border-line-strong text-base font-semibold',
                emphasis === 'subtotal' && 'pt-1 mt-1 border-t border-line-weak text-sm',
                !emphasis && 'text-sm'
            )}
        >
            <dt className={classnames('whitespace-nowrap', emphasis !== 'total' && 'text-content-medium')}>{label}</dt>
            <dd className="text-content-strong text-right break-words tabular-nums">{value}</dd>
        </div>
    );
};

type PaymentMeans = NonNullable<EInvoiceData['invoice']['payment_means']>;

const PaymentBlock = ({ means, reference }: { means: PaymentMeans | null | undefined; reference?: string | null }) => {
    const { t } = useTranslation('assistance');
    if (!means || !(means.iban || means.bic || means.account_id || means.information)) return null;
    return (
        <div>
            <div className="text-xs uppercase tracking-wide text-content-medium mb-1">
                {t('eInvoiceView.payment.title')}
            </div>
            {means.information && <div className="mb-1">{means.information}</div>}
            <dl className="space-y-0.5">
                <MetaRow label={t('eInvoiceView.payment.accountName')} value={means.account_name} />
                <MetaRow label={t('eInvoiceView.payment.iban')} value={means.iban} />
                <MetaRow
                    label={t('eInvoiceView.payment.accountId')}
                    value={means.iban ? undefined : means.account_id}
                />
                <MetaRow label={t('eInvoiceView.payment.bic')} value={means.bic} />
                <MetaRow label={t('eInvoiceView.payment.reference')} value={reference} />
            </dl>
        </div>
    );
};

type XmlFile = { name?: string | null; url?: string | null };

const EInvoiceView = React.memo(({ eInvoice, xmlFile }: { eInvoice: EInvoiceData; xmlFile?: XmlFile }) => {
    const { t } = useTranslation('assistance');
    const invoice = eInvoice.invoice;
    const currency = invoice.currency || 'EUR';
    const fmt = useFormatters(currency);
    const isCreditNote = !!eInvoice.is_credit_note;
    const lineItems = invoice.line_items || [];
    const allowancesCharges = invoice.allowances_charges || [];
    const taxBreakdown = invoice.tax_breakdown || [];
    const discountTerms = invoice.payment_discount_terms || [];
    const attachments = invoice.attachments || [];
    const isNonZero = (value: string | null | undefined) => !!value && Number(value) !== 0;
    const hasDocumentAllowancesOrCharges =
        allowancesCharges.length > 0 || isNonZero(invoice.allowance_total) || isNonZero(invoice.charge_total);
    const hasPrepaidOrRounding = isNonZero(invoice.prepaid_amount) || isNonZero(invoice.rounding_amount);

    const cell = 'px-3 py-2 align-top';
    const numeric = 'text-right whitespace-nowrap tabular-nums';

    return (
        <div className="flex flex-col items-center bg-surface-medium h-full w-full overflow-auto">
            <div
                className={classnames(
                    'mt-8 w-full max-w-[56rem] flex items-start gap-3 rounded px-4 py-3',
                    'bg-surface-warning-weak text-content-warning-strong text-sm'
                )}
                role="note"
            >
                <InfoIcon className="mt-0.5 shrink-0" />
                <div className="flex-grow min-w-0">
                    <div className="font-semibold">{t('eInvoiceView.notice.title')}</div>
                    <div className="mt-0.5">{t('eInvoiceView.notice.message')}</div>
                </div>
                {xmlFile?.url && (
                    <a
                        href={xmlFile.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={classnames(
                            'shrink-0 inline-flex items-center gap-2 rounded-md border border-line-strong',
                            'bg-surface-weak px-3 py-1.5 text-sm font-medium text-content-strong shadow-sm'
                        )}
                        title={xmlFile.name || undefined}
                    >
                        {t('eInvoiceView.notice.openXml')}
                        <ExternalLinkIcon />
                    </a>
                )}
            </div>
            <article
                className={classnames(
                    'my-8 w-full max-w-[56rem] h-fit',
                    'rounded border border-solid border-line-weak shadow bg-surface-weak',
                    'text-sm text-content-strong'
                )}
                data-type="e-invoice"
            >
                <header className="flex items-start justify-between gap-4 px-8 pt-8 pb-6 border-b border-line-weak">
                    <div>
                        <div className="text-xs uppercase tracking-wide text-content-medium">
                            {isCreditNote ? t('eInvoiceView.creditNote') : t('eInvoiceView.invoice')}
                        </div>
                        <h1 className="text-2xl font-semibold mt-1 break-words">{invoice.invoice_id}</h1>
                    </div>
                    <div className="text-right shrink-0">
                        <span className="inline-block rounded-full border border-line-strong px-2 py-0.5 text-xs font-medium">
                            {FORMAT_LABELS[eInvoice.format] || eInvoice.format}
                        </span>
                        {eInvoice.specification_id && (
                            <div
                                className="mt-1 text-[11px] text-content-medium max-w-[20rem] truncate"
                                title={eInvoice.specification_id}
                            >
                                {eInvoice.specification_id}
                            </div>
                        )}
                    </div>
                </header>

                <section className="grid grid-cols-1 md:grid-cols-3 gap-6 px-8 py-6 border-b border-line-weak">
                    <PartyBlock title={t('eInvoiceView.party.seller')} party={invoice.seller} />
                    <PartyBlock title={t('eInvoiceView.party.buyer')} party={invoice.buyer} />
                    <PartyBlock title={t('eInvoiceView.party.deliverTo')} party={invoice.delivery_party} />
                </section>

                <section className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-1 px-8 py-6 border-b border-line-weak">
                    <dl className="space-y-1">
                        <MetaRow label={t('eInvoiceView.meta.issueDate')} value={fmt.date(invoice.issue_date)} />
                        <MetaRow label={t('eInvoiceView.meta.dueDate')} value={fmt.date(invoice.due_date)} />
                        <MetaRow label={t('eInvoiceView.meta.deliveryDate')} value={fmt.date(invoice.delivery_date)} />
                        <MetaRow label={t('eInvoiceView.meta.currency')} value={currency} />
                    </dl>
                    <dl className="space-y-1">
                        <MetaRow label={t('eInvoiceView.meta.orderReference')} value={invoice.order_reference} />
                        <MetaRow
                            label={t('eInvoiceView.meta.deliveryNoteReference')}
                            value={invoice.delivery_note_reference}
                        />
                        <MetaRow label={t('eInvoiceView.meta.contractReference')} value={invoice.contract_reference} />
                        <MetaRow label={t('eInvoiceView.meta.buyerReference')} value={invoice.buyer_reference} />
                        <MetaRow label={t('eInvoiceView.meta.orderDate')} value={fmt.date(invoice.order_date)} />
                        <MetaRow
                            label={t('eInvoiceView.meta.sellerOrderReference')}
                            value={invoice.seller_order_reference}
                        />
                        <MetaRow
                            label={t('eInvoiceView.meta.despatchAdviceReference')}
                            value={invoice.despatch_advice_reference}
                        />
                        <MetaRow
                            label={t('eInvoiceView.meta.invoicePeriod')}
                            value={formatPeriod(invoice.invoice_period_start, invoice.invoice_period_end, fmt)}
                        />
                        <MetaRow label={t('eInvoiceView.meta.deliveryTerms')} value={invoice.delivery_terms} />
                    </dl>
                </section>

                <section className="px-8 py-6 border-b border-line-weak overflow-x-auto">
                    <table className="w-full border-collapse">
                        <thead>
                            <tr className="text-xs uppercase tracking-wide text-content-medium border-b border-line-weak">
                                <th className={classnames(cell, 'text-left w-10')}>{t('eInvoiceView.lines.pos')}</th>
                                <th className={classnames(cell, 'text-left')}>{t('eInvoiceView.lines.article')}</th>
                                <th className={classnames(cell, numeric)}>{t('eInvoiceView.lines.quantity')}</th>
                                <th className={classnames(cell, numeric)}>{t('eInvoiceView.lines.unitPrice')}</th>
                                <th className={classnames(cell, numeric)}>{t('eInvoiceView.lines.taxRate')}</th>
                                <th className={classnames(cell, numeric)}>{t('eInvoiceView.lines.total')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {lineItems.map((item, index) => {
                                const identifiers = lineIdentifiers(item, t);
                                const period = formatPeriod(item.period_start, item.period_end, fmt);
                                return (
                                    <React.Fragment key={item.line_id ?? index}>
                                        <tr className="border-b border-line-weak last:border-b-0">
                                            <td className={classnames(cell, 'text-content-medium')}>
                                                {item.line_id ?? index + 1}
                                            </td>
                                            <td className={cell}>
                                                <div className="font-medium break-words">{item.product_name}</div>
                                                {item.description && item.description !== item.product_name && (
                                                    <div className="text-xs text-content-medium mt-0.5 whitespace-pre-line break-words">
                                                        {item.description}
                                                    </div>
                                                )}
                                                {identifiers.length > 0 && (
                                                    <div className="text-xs text-content-medium mt-0.5 break-words">
                                                        {identifiers.join(' · ')}
                                                    </div>
                                                )}
                                                {period && (
                                                    <div className="text-xs text-content-medium mt-0.5">
                                                        {t('eInvoiceView.lines.period')}: {period}
                                                    </div>
                                                )}
                                            </td>
                                            <td className={classnames(cell, numeric)}>
                                                {fmt.number(item.quantity)}
                                                {item.unit_code && (
                                                    <span className="text-content-medium"> {item.unit_code}</span>
                                                )}
                                            </td>
                                            <td className={classnames(cell, numeric)}>
                                                {fmt.money(item.unit_price)}
                                                {item.price_base_quantity && Number(item.price_base_quantity) !== 1 && (
                                                    <span className="text-content-medium">
                                                        {' '}
                                                        / {fmt.number(item.price_base_quantity)}
                                                    </span>
                                                )}
                                                {hasPriceDiscount(item) && (
                                                    <div className="text-xs text-content-medium mt-0.5">
                                                        {t('eInvoiceView.lines.listPrice')}{' '}
                                                        {fmt.money(item.gross_unit_price)}
                                                        {item.price_allowance_amount &&
                                                            Number(item.price_allowance_amount) !== 0 && (
                                                                <>
                                                                    {' · '}
                                                                    {t('eInvoiceView.lines.priceDiscount')} −
                                                                    {fmt.money(item.price_allowance_amount)}
                                                                </>
                                                            )}
                                                    </div>
                                                )}
                                            </td>
                                            <td className={classnames(cell, numeric)}>{fmt.percent(item.tax_rate)}</td>
                                            <td className={classnames(cell, numeric, 'font-medium')}>
                                                {fmt.money(item.line_total)}
                                            </td>
                                        </tr>
                                        {(item.allowances_charges || []).map((entry, entryIndex) => (
                                            <tr
                                                key={`${item.line_id}-${entryIndex}`}
                                                className="text-xs text-content-medium"
                                            >
                                                <td className={cell} />
                                                <td className={classnames(cell, 'pl-6')} colSpan={4}>
                                                    {describeAllowanceCharge(entry, t, fmt)}
                                                </td>
                                                <td className={classnames(cell, numeric)}>{signedMoney(entry, fmt)}</td>
                                            </tr>
                                        ))}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                </section>

                <section className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-6 px-8 py-6">
                    <div className="space-y-4">
                        <PaymentBlock
                            means={invoice.payment_means}
                            reference={invoice.payment_terms?.payment_reference}
                        />
                        {discountTerms.length > 0 && (
                            <div>
                                <div className="text-xs uppercase tracking-wide text-content-medium mb-1">
                                    {t('eInvoiceView.payment.discount')}
                                </div>
                                {discountTerms.map((terms, index) => (
                                    <div key={index}>{describeDiscountTerms(terms, t, fmt)}</div>
                                ))}
                            </div>
                        )}
                        {invoice.payment_terms?.description && (
                            <div>
                                <div className="text-xs uppercase tracking-wide text-content-medium mb-1">
                                    {t('eInvoiceView.payment.terms')}
                                </div>
                                <div className="whitespace-pre-line break-words">
                                    {invoice.payment_terms.description}
                                </div>
                            </div>
                        )}
                        {invoice.note && (
                            <div>
                                <div className="text-xs uppercase tracking-wide text-content-medium mb-1">
                                    {t('eInvoiceView.notes')}
                                </div>
                                <div className="whitespace-pre-line break-words text-content-medium">
                                    {invoice.note}
                                </div>
                            </div>
                        )}
                        {attachments.length > 0 && (
                            <div>
                                <div className="text-xs uppercase tracking-wide text-content-medium mb-1">
                                    {t('eInvoiceView.attachments.title')}
                                </div>
                                <ul className="space-y-0.5">
                                    {attachments.map((attachment, index) => (
                                        <li key={index} className="break-words">
                                            {attachment.filename || attachment.description || attachment.id}
                                            <span className="text-content-medium">
                                                {' '}
                                                ({attachmentDetails(attachment, t)})
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                                {attachments.some((attachment) => attachment.embedded) && (
                                    <div className="text-xs text-content-medium mt-1">
                                        {t('eInvoiceView.attachments.notice')}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    <dl className="space-y-1 self-start md:justify-self-end w-full md:max-w-[22rem]">
                        <MetaRow label={t('eInvoiceView.totals.lineTotal')} value={fmt.money(invoice.line_total)} />
                        {allowancesCharges.map((entry, index) => (
                            <MetaRow
                                key={index}
                                label={describeAllowanceCharge(entry, t, fmt)}
                                value={signedMoney(entry, fmt)}
                            />
                        ))}
                        {hasDocumentAllowancesOrCharges && (
                            <MetaRow
                                label={t('eInvoiceView.totals.net')}
                                value={fmt.money(invoice.tax_exclusive_amount ?? invoice.line_total)}
                                emphasis="subtotal"
                            />
                        )}
                        {taxBreakdown.length > 0 ? (
                            taxBreakdown.map((tax, index) => (
                                <MetaRow
                                    key={index}
                                    label={describeTax(tax, t, fmt)}
                                    value={fmt.money(tax.tax_amount || '0')}
                                />
                            ))
                        ) : (
                            <MetaRow
                                label={
                                    invoice.tax_rate
                                        ? `${t('eInvoiceView.totals.tax')} (${fmt.percent(invoice.tax_rate)})`
                                        : t('eInvoiceView.totals.tax')
                                }
                                value={fmt.money(invoice.tax_total)}
                            />
                        )}
                        <MetaRow
                            label={t('eInvoiceView.totals.grandTotal')}
                            value={fmt.money(invoice.tax_inclusive_amount ?? invoice.grand_total)}
                            emphasis="total"
                        />
                        {hasPrepaidOrRounding && (
                            <>
                                <MetaRow
                                    label={t('eInvoiceView.totals.prepaid')}
                                    value={invoice.prepaid_amount ? `−${fmt.money(invoice.prepaid_amount)}` : undefined}
                                />
                                <MetaRow
                                    label={t('eInvoiceView.totals.rounding')}
                                    value={invoice.rounding_amount ? fmt.money(invoice.rounding_amount) : undefined}
                                />
                                <MetaRow
                                    label={t('eInvoiceView.totals.amountDue')}
                                    value={fmt.money(invoice.payable_amount)}
                                    emphasis="total"
                                />
                            </>
                        )}
                    </dl>
                </section>
            </article>
        </div>
    );
});
EInvoiceView.displayName = 'EInvoiceView';

export default EInvoiceView;
